import { z } from 'zod';
import {
  appError,
  LISTING_STATUSES,
  OWNERSHIP_TYPES,
  PRODUCT_STATUSES,
  VARIANT_ATTRIBUTE_KEYS,
} from '@beautyburst/types';
import type { Actor } from '@beautyburst/types';
import type { ApiResponse, RequestContext, RouteDefinition } from '@beautyburst/api';
import type { createCatalogService } from '../catalog-service.ts';

type CatalogService = ReturnType<typeof createCatalogService>;

export interface CatalogRouteDependencies {
  service: CatalogService;
}

const brandSchema = z.object({
  name: z.string().trim().min(1).max(160),
  slug: z
    .string()
    .trim()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
    .max(180),
  logoMediaKey: z.string().trim().min(1).max(500).nullable().optional(),
});
const categorySchema = z.object({
  parentId: z.string().min(1).nullable().optional(),
  name: z.string().trim().min(1).max(160),
  slug: z
    .string()
    .trim()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
    .max(180),
  path: z
    .string()
    .trim()
    .regex(/^[a-z0-9-]+(?:\/[a-z0-9-]+)*$/)
    .max(500),
  sortOrder: z.number().int().min(0).default(0),
});
const productSchema = z.object({
  brandId: z.string().min(1).nullable().optional(),
  categoryId: z.string().min(1).nullable().optional(),
  name: z.string().trim().min(1).max(240),
  slug: z
    .string()
    .trim()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
    .max(250),
  description: z.string().max(10_000).nullable().optional(),
  status: z.enum(PRODUCT_STATUSES).default('DRAFT'),
  source: z.enum(['PLATFORM', 'RETAILER_SUBMITTED', 'IMPORT']).default('PLATFORM'),
  overnightRestricted: z.boolean().default(false),
  requiresAgeCheck: z.boolean().default(false),
  isHazmat: z.boolean().default(false),
  searchKeywords: z.array(z.string().trim().min(1).max(120)).max(50).default([]),
});
const variantSchema = z.object({
  productId: z.string().min(1),
  name: z.string().trim().min(1).max(240),
  skuReference: z.string().trim().min(1).max(100).nullable().optional(),
  upc: z
    .string()
    .regex(/^[0-9]{8,14}$/)
    .nullable()
    .optional(),
  gtin: z
    .string()
    .regex(/^[0-9]{8,14}$/)
    .nullable()
    .optional(),
  gtinType: z.enum(['UPC_A', 'UPC_E', 'EAN_8', 'EAN_13', 'GTIN_14']).nullable().optional(),
  packQuantity: z.number().int().positive().default(1),
  isDefault: z.boolean().default(false),
  attributes: z
    .array(
      z.object({
        key: z.enum(VARIANT_ATTRIBUTE_KEYS),
        value: z.string().trim().min(1).max(120),
        displayOrder: z.number().int().min(0).default(0),
      }),
    )
    .max(15)
    .default([]),
});
const listingSchema = z.object({
  retailerId: z.string().min(1),
  locationId: z.string().min(1),
  productId: z.string().min(1),
  variantId: z.string().min(1),
  ownershipType: z.enum(OWNERSHIP_TYPES).default('RETAILER'),
  sellerDisplayName: z.string().trim().min(1).max(160),
  sellerSku: z.string().trim().min(1).max(100),
  externalRef: z.string().trim().min(1).max(160).nullable().optional(),
  priceCents: z.number().int().positive(),
  salePriceCents: z.number().int().positive().nullable().optional(),
  status: z.enum(LISTING_STATUSES).default('DRAFT'),
  pickupEligible: z.boolean().default(false),
  deliveryEligible: z.boolean().default(true),
  overnightEligible: z.boolean().default(false),
});
const listingPatchSchema = listingSchema.omit({
  retailerId: true,
  locationId: true,
  productId: true,
  variantId: true,
  ownershipType: true,
  sellerDisplayName: true,
});
const mediaSchema = z.object({
  storageKey: z.string().trim().min(1).max(500),
  variantId: z.string().min(1).nullable().optional(),
  mediaKind: z.enum(['IMAGE', 'VIDEO']).default('IMAGE'),
  altText: z.string().trim().max(500).nullable().optional(),
  sortOrder: z.number().int().min(0).default(0),
});

/**
 * Administrator-facing catalog routes only. The retailer-facing listing routes are
 * returned by `retailerProductRoutes` and are mounted separately, so the two sets
 * stay disjoint and neither can shadow the other.
 */
export function catalogRoutes(deps: CatalogRouteDependencies): RouteDefinition[] {
  return [
    {
      method: 'GET',
      path: '/api/v1/admin/direct-inventory/listings',
      permission: 'admin.direct_inventory.manage',
      flag: 'catalog.beauty_burst_direct',
      rateLimit: 'read',
      async handler(request, context) {
        const locationId = request.query.locationId?.trim() || null;
        return {
          status: 200,
          headers: {},
          body: { listings: await deps.service.listDirectListings(context.actor, locationId) },
        };
      },
    },
    {
      method: 'POST',
      path: '/api/v1/admin/catalog/brands',
      permission: 'admin.catalog.manage',
      requiresIdempotencyKey: true,
      rateLimit: 'mutation',
      async handler(request, context) {
        const input = parse(brandSchema, request.body);
        const value = await deps.service.createBrand(context.actor, {
          ...input,
          logoMediaKey: input.logoMediaKey ?? null,
        });
        await audit(context, 'catalog.brand.created', 'brand', value.id);
        return created(value);
      },
    },
    {
      method: 'POST',
      path: '/api/v1/admin/catalog/categories',
      permission: 'admin.catalog.manage',
      requiresIdempotencyKey: true,
      rateLimit: 'mutation',
      async handler(request, context) {
        const input = parse(categorySchema, request.body);
        const value = await deps.service.createCategory(context.actor, {
          ...input,
          parentId: input.parentId ?? null,
          sortOrder: input.sortOrder ?? 0,
        });
        await audit(context, 'catalog.category.created', 'category', value.id);
        return created(value);
      },
    },
    {
      method: 'POST',
      path: '/api/v1/admin/catalog/products',
      permission: 'admin.catalog.manage',
      requiresIdempotencyKey: true,
      rateLimit: 'mutation',
      async handler(request, context) {
        const input = parse(productSchema, request.body);
        const value = await deps.service.createProduct(context.actor, {
          ...input,
          brandId: input.brandId ?? null,
          categoryId: input.categoryId ?? null,
          description: input.description ?? null,
          status: input.status ?? 'DRAFT',
          source: input.source ?? 'PLATFORM',
          overnightRestricted: input.overnightRestricted ?? false,
          requiresAgeCheck: input.requiresAgeCheck ?? false,
          isHazmat: input.isHazmat ?? false,
          searchKeywords: input.searchKeywords ?? [],
        });
        await audit(context, 'catalog.product.created', 'product', value.id);
        return created(value);
      },
    },
    {
      method: 'PATCH',
      path: '/api/v1/admin/catalog/products/:productId',
      permission: 'admin.catalog.manage',
      requiresIdempotencyKey: true,
      rateLimit: 'mutation',
      async handler(request, context) {
        const input = parse(productSchema, request.body);
        const value = await deps.service.updateProduct(
          context.actor,
          requiredParam(context, 'productId'),
          {
            ...input,
            description: input.description ?? null,
            status: input.status ?? 'DRAFT',
            overnightRestricted: input.overnightRestricted ?? false,
            requiresAgeCheck: input.requiresAgeCheck ?? false,
            isHazmat: input.isHazmat ?? false,
            searchKeywords: input.searchKeywords ?? [],
          },
        );
        await audit(
          context,
          input.status === 'ARCHIVED' ? 'catalog.product.archived' : 'catalog.product.updated',
          'product',
          value.id,
        );
        return ok(value);
      },
    },
    {
      method: 'POST',
      path: '/api/v1/admin/catalog/products/:productId/variants',
      permission: 'admin.catalog.manage',
      requiresIdempotencyKey: true,
      rateLimit: 'mutation',
      async handler(request, context) {
        const input = parse(variantSchema.omit({ productId: true }), request.body);
        const value = await deps.service.createVariant(context.actor, {
          ...input,
          productId: requiredParam(context, 'productId'),
          skuReference: input.skuReference ?? null,
          upc: input.upc ?? null,
          gtin: input.gtin ?? null,
          gtinType: input.gtinType ?? null,
          packQuantity: input.packQuantity ?? 1,
          isDefault: input.isDefault ?? false,
          attributes: (input.attributes ?? []).map((attribute) => ({
            ...attribute,
            displayOrder: attribute.displayOrder ?? 0,
          })),
        });
        await audit(context, 'catalog.variant.created', 'variant', value.id);
        return created(value);
      },
    },
    {
      method: 'POST',
      path: '/api/v1/admin/direct-inventory/listings',
      permission: 'admin.direct_inventory.manage',
      flag: 'catalog.beauty_burst_direct',
      requiresIdempotencyKey: true,
      rateLimit: 'mutation',
      async handler(request, context) {
        const input = parse(listingSchema, request.body);
        const value = await deps.service.createListing(context.actor, {
          ...input,
          externalRef: input.externalRef ?? null,
          salePriceCents: input.salePriceCents ?? null,
          ownershipType: 'BEAUTY_BURST_DIRECT',
          status: input.status ?? 'DRAFT',
          pickupEligible: input.pickupEligible ?? false,
          deliveryEligible: input.deliveryEligible ?? true,
          overnightEligible: input.overnightEligible ?? false,
          allowDirect: context.flags.isEnabled('catalog.beauty_burst_direct', {
            locationId: input.locationId,
          }),
        });
        await audit(context, 'listing.created', 'listing', value.id);
        return created(value);
      },
    },
  ];
}

export function retailerProductRoutes(deps: CatalogRouteDependencies): RouteDefinition[] {
  return [
    {
      method: 'POST',
      path: '/api/v1/retailer/products',
      permission: 'retailer.catalog.manage',
      requiresIdempotencyKey: true,
      rateLimit: 'mutation',
      async handler(request, context) {
        const input = parse(listingSchema, request.body);
        const value = await deps.service.createListing(context.actor, {
          ...input,
          externalRef: input.externalRef ?? null,
          salePriceCents: input.salePriceCents ?? null,
          ownershipType: input.ownershipType ?? 'RETAILER',
          status: input.status ?? 'DRAFT',
          pickupEligible: input.pickupEligible ?? false,
          deliveryEligible: input.deliveryEligible ?? true,
          overnightEligible: input.overnightEligible ?? false,
          allowDirect: false,
        });
        await audit(context, 'listing.created', 'listing', value.id);
        return created(value);
      },
    },
    {
      method: 'PATCH',
      path: '/api/v1/retailer/products/:listingId',
      permission: 'retailer.catalog.manage',
      requiresIdempotencyKey: true,
      rateLimit: 'mutation',
      async handler(request, context) {
        const input = parse(listingPatchSchema, request.body);
        const value = await deps.service.updateListing(
          context.actor,
          requiredParam(context, 'listingId'),
          {
            ...input,
            externalRef: input.externalRef ?? null,
            salePriceCents: input.salePriceCents ?? null,
            status: input.status ?? 'DRAFT',
            pickupEligible: input.pickupEligible ?? false,
            deliveryEligible: input.deliveryEligible ?? true,
            overnightEligible: input.overnightEligible ?? false,
          },
        );
        await audit(context, 'listing.updated', 'listing', value.id);
        return ok(value);
      },
    },
    {
      method: 'POST',
      path: '/api/v1/retailer/products/:listingId/archive',
      permission: 'retailer.catalog.manage',
      requiresIdempotencyKey: true,
      rateLimit: 'mutation',
      async handler(_request, context) {
        const value = await deps.service.archiveListing(
          context.actor,
          requiredParam(context, 'listingId'),
        );
        await audit(context, 'listing.archived', 'listing', value.id);
        return ok(value);
      },
    },
    {
      method: 'POST',
      path: '/api/v1/retailer/products/:listingId/restore',
      permission: 'retailer.catalog.manage',
      requiresIdempotencyKey: true,
      rateLimit: 'mutation',
      async handler(_request, context) {
        const value = await deps.service.restoreListing(
          context.actor,
          requiredParam(context, 'listingId'),
        );
        await audit(context, 'listing.restored', 'listing', value.id);
        return ok(value);
      },
    },
    {
      method: 'GET',
      path: '/api/v1/retailer/products',
      permission: 'retailer.catalog.read',
      rateLimit: 'read',
      async handler(request, context) {
        const values = await deps.service.listListings(context.actor, {
          locationId: request.query.locationId ?? null,
          search: request.query.search ?? null,
        });
        return ok({ listings: values });
      },
    },
    {
      method: 'GET',
      path: '/api/v1/retailer/products/:listingId',
      permission: 'retailer.catalog.read',
      rateLimit: 'read',
      async handler(_request, context) {
        return ok(
          await deps.service.getListing(context.actor, requiredParam(context, 'listingId')),
        );
      },
    },
    {
      method: 'POST',
      path: '/api/v1/retailer/products/:listingId/media',
      permission: 'retailer.catalog.manage',
      requiresIdempotencyKey: true,
      rateLimit: 'mutation',
      async handler(request, context) {
        const body = parse(mediaSchema, request.body);
        const value = await deps.service.attachMedia(
          context.actor,
          requiredParam(context, 'listingId'),
          {
            ...body,
            variantId: body.variantId ?? null,
            mediaKind: body.mediaKind ?? 'IMAGE',
            altText: body.altText ?? null,
            sortOrder: body.sortOrder ?? 0,
          },
        );
        await audit(context, 'catalog.media.attached', 'media', value.id);
        return created(value);
      },
    },
    {
      method: 'DELETE',
      path: '/api/v1/retailer/products/:listingId/media/:mediaId',
      permission: 'retailer.catalog.manage',
      requiresIdempotencyKey: true,
      rateLimit: 'mutation',
      async handler(_request, context) {
        await deps.service.removeMedia(
          context.actor,
          requiredParam(context, 'listingId'),
          requiredParam(context, 'mediaId'),
        );
        await audit(context, 'catalog.media.removed', 'media', requiredParam(context, 'mediaId'));
        return { status: 204, headers: {}, body: null };
      },
    },
  ];
}

function parse<T>(schema: z.ZodType<T>, value: unknown): T {
  const result = schema.safeParse(value);
  if (result.success) return result.data;
  const details: Record<string, string[]> = {};
  for (const issue of result.error.issues) {
    const key = issue.path.join('.') || 'body';
    details[key] = [...(details[key] ?? []), issue.message];
  }
  throw appError('VALIDATION_FAILED', 'Request body is invalid.', details);
}

function requiredParam(context: RequestContext, key: string): string {
  const value = context.params[key];
  if (value === undefined || value.length === 0)
    throw appError('VALIDATION_FAILED', `Missing ${key}.`);
  return value;
}

async function audit(
  context: RequestContext,
  action:
    | 'catalog.brand.created'
    | 'catalog.category.created'
    | 'catalog.product.created'
    | 'catalog.product.updated'
    | 'catalog.product.archived'
    | 'catalog.variant.created'
    | 'listing.created'
    | 'listing.updated'
    | 'listing.archived'
    | 'listing.restored'
    | 'catalog.media.attached'
    | 'catalog.media.removed',
  type: string,
  id: string,
): Promise<void> {
  await context.audit.record({
    action,
    actor: actorRef(context.actor),
    resource: { type, id },
    outcome: 'SUCCESS',
    correlationId: context.correlationId,
  });
}

function actorRef(actor: Actor) {
  return {
    kind: actor.kind,
    userId: actor.userId,
    retailerId: actor.retailerId,
    sessionId: actor.sessionId,
  };
}

function ok(body: unknown): ApiResponse {
  return { status: 200, headers: {}, body };
}

function created(body: unknown): ApiResponse {
  return { status: 201, headers: {}, body };
}
